import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Printer, Save, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { api, euro, STATI, formatError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { SearchSelect } from "@/components/SearchSelect";
import { RicambiRows, ManodoperaRows } from "@/components/SchedaRows";
import { ConfirmDelete } from "@/components/ConfirmDelete";
import { EntityFormDialog } from "@/components/EntityFormDialog";
import { CLIENTE_FIELDS } from "@/pages/Clienti";
import { biciFields } from "@/pages/Bici";
import { WhatsAppButton } from "@/components/WhatsAppButton";
import { messaggioScheda, openWhatsApp } from "@/lib/whatsapp";

const today = () => new Date().toISOString().slice(0, 10);
const EMPTY = { cliente_id: "", bici_id: "", nome_libero: "", telefono_libero: "", bici_libera: "", data_ingresso: today(), data_consegna_prevista: "", problema: "", lavori_eseguiti: "", stato: "aperto", ricambi: [], manodopera: [], note: "" };
const L = ({ children }) => <Label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">{children}</Label>;
const Section = ({ title, children, testId }) => (
  <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm" data-testid={testId}>
    <h2 className="mb-4 font-heading text-base font-bold text-slate-900">{title}</h2>{children}
  </section>
);
const ClienteLibero = ({ f, set }) => (
  <div className="grid gap-4 sm:grid-cols-2" data-testid="scheda-cliente-libero">
    <div><L>Nome e cognome *</L><Input value={f.nome_libero} onChange={(e) => set("nome_libero", e.target.value)} data-testid="scheda-nome-libero-input" /></div>
    <div><L>Telefono (per WhatsApp)</L><Input type="tel" value={f.telefono_libero} onChange={(e) => set("telefono_libero", e.target.value)} data-testid="scheda-telefono-libero-input" /></div>
    <div className="sm:col-span-2"><L>Bici *</L><Input placeholder="Es. Trek Marlin 7, rossa" value={f.bici_libera} onChange={(e) => set("bici_libera", e.target.value)} data-testid="scheda-bici-libera-input" /></div>
  </div>
);

export default function SchedaForm() {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const [f, setF] = useState({ ...EMPTY, cliente_id: sp.get("cliente") || "", bici_id: sp.get("bici") || "", nome_libero: sp.get("nome") || "", telefono_libero: sp.get("tel") || "" });
  const [libero, setLibero] = useState(!sp.get("cliente") && Boolean(sp.get("nome")));
  const [numero, setNumero] = useState("");
  const [clienti, setClienti] = useState([]);
  const [bici, setBici] = useState([]);
  const [magazzino, setMagazzino] = useState([]);
  const [tariffa, setTariffa] = useState(35);
  const [negozio, setNegozio] = useState("");
  const [saving, setSaving] = useState(false);
  const [dlg, setDlg] = useState(null);

  const loadRefs = useCallback(async () => {
    const [c, b, r, imp] = await Promise.all([api.get("/clienti"), api.get("/bici"), api.get("/ricambi"), api.get("/impostazioni")]);
    setClienti(c.data); setBici(b.data); setMagazzino(r.data); setTariffa(imp.data.tariffa_oraria || 35); setNegozio(imp.data.nome_negozio || "");
  }, []);

  useEffect(() => {
    loadRefs().catch((e) => toast.error(formatError(e)));
    if (id) {
      api.get(`/schede/${id}`).then(({ data }) => {
        setNumero(data.numero);
        setF(Object.fromEntries(Object.keys(EMPTY).map((k) => [k, data[k] ?? EMPTY[k]])));
        setLibero(!data.cliente_id && Boolean(data.nome_libero));
      }).catch((e) => toast.error(formatError(e)));
    }
  }, [id, loadRefs]);

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const biciCliente = bici.filter((b) => b.cliente_id === f.cliente_id);
  const totR = f.ricambi.reduce((a, r) => a + (Number(r.quantita) || 0) * (Number(r.prezzo) || 0), 0);
  const totM = f.manodopera.reduce((a, m) => a + (Number(m.ore) || 0) * (Number(m.tariffa) || 0), 0);
  const cliente = libero ? { nome: f.nome_libero, telefono: f.telefono_libero } : clienti.find((c) => c.id === f.cliente_id);
  const biciSel = bici.find((b) => b.id === f.bici_id);
  const biciTxt = libero ? f.bici_libera : biciSel ? `${biciSel.marca} ${biciSel.modello}`.trim() : "";
  const waText = (num, tot) => messaggioScheda({ nome: cliente?.nome || "", bici: biciTxt, numero: num, stato: f.stato, totale: tot, negozio });

  const save = async () => {
    if (libero ? !f.nome_libero.trim() || !f.bici_libera.trim() : !f.cliente_id || !f.bici_id) return toast.error(libero ? "Scrivi nome del cliente e bici" : "Seleziona cliente e bici");
    const payload = {
      ...f,
      ...(libero ? { cliente_id: "", bici_id: "" } : { nome_libero: "", telefono_libero: "", bici_libera: "" }),
      ricambi: f.ricambi.map((r) => ({ ...r, quantita: Number(r.quantita) || 1, prezzo: Number(r.prezzo) || 0 })),
      manodopera: f.manodopera.filter((m) => m.descrizione.trim()).map((m) => ({ ...m, ore: Number(m.ore) || 0, tariffa: Number(m.tariffa) || 0 })),
    };
    setSaving(true);
    try {
      const { data } = id ? await api.put(`/schede/${id}`, payload) : await api.post("/schede", payload);
      const msg = id ? "Scheda aggiornata" : `Scheda #${data.numero} creata`;
      if (f.stato === "completato" && cliente?.telefono) {
        toast.success(`${msg}. La bici è pronta: vuoi avvisare il cliente?`, { duration: 10000, action: { label: "Avvisa su WhatsApp", onClick: () => openWhatsApp(cliente.telefono, waText(data.numero, data.totale)) } });
      } else toast.success(msg);
      if (!id) navigate(`/schede/${data.id}`, { replace: true });
      else loadRefs();
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/schede/${id}`);
      toast.success("Scheda eliminata, ricambi restituiti al magazzino");
      navigate("/schede");
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  const clienteOpts = clienti.map((c) => ({ value: c.id, label: `${c.nome} ${c.cognome}`.trim(), hint: c.telefono }));
  const biciOpts = biciCliente.map((b) => ({ value: b.id, label: `${b.marca} ${b.modello}`.trim(), hint: b.telaio }));

  return (
    <div>
      <Link to="/schede" className="mb-4 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900" data-testid="scheda-back-link"><ArrowLeft className="h-4 w-4" /> Schede</Link>
      <PageHeader overline={id ? `Scheda #${numero}` : "Nuova scheda"} title={id ? "Modifica scheda di lavoro" : "Nuova scheda di lavoro"}>
        {id && <ConfirmDelete testId="scheda-delete-button" onConfirm={remove} description="I ricambi della scheda verranno restituiti al magazzino." />}
        {id && <WhatsAppButton telefono={cliente?.telefono} text={waText(numero, totR + totM)} label={f.stato === "completato" ? "Avvisa bici pronta" : "WhatsApp"} testId="scheda-whatsapp-button" />}
        {id && <Button variant="outline" onClick={() => navigate(`/schede/${id}/stampa`)} data-testid="btn-stampa-scheda"><Printer className="mr-1 h-4 w-4" /> Stampa scheda</Button>}
        <Button onClick={save} disabled={saving} className="bg-orange-600 hover:bg-orange-700" data-testid="scheda-save-button"><Save className="mr-1 h-4 w-4" /> {saving ? "Salvataggio..." : "Salva scheda"}</Button>
      </PageHeader>

      <div className="grid gap-6 xl:grid-cols-[1fr_340px]">
        <div className="space-y-6">
          <Section title="Cliente e bici" testId="scheda-section-cliente">
            <button type="button" onClick={() => setLibero((v) => !v)} className="mb-4 text-xs font-semibold text-orange-600 hover:underline" data-testid="scheda-cliente-libero-toggle">
              {libero ? "Scegli un cliente registrato" : "Cliente non registrato? Scrivi nome e bici"}
            </button>
            {libero ? <ClienteLibero f={f} set={set} /> : (
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <div className="flex items-center justify-between"><L>Cliente *</L><button className="mb-1.5 text-xs font-semibold text-orange-600 hover:underline" onClick={() => setDlg("cliente")} data-testid="scheda-quick-cliente-button"><Plus className="inline h-3 w-3" /> Nuovo</button></div>
                <SearchSelect options={clienteOpts} value={f.cliente_id} onChange={(v) => setF((s) => ({ ...s, cliente_id: v, bici_id: "" }))} placeholder="Seleziona cliente..." testId="scheda-cliente-select" />
              </div>
              <div>
                <div className="flex items-center justify-between"><L>Bici *</L>{f.cliente_id && <button className="mb-1.5 text-xs font-semibold text-orange-600 hover:underline" onClick={() => setDlg("bici")} data-testid="scheda-quick-bici-button"><Plus className="inline h-3 w-3" /> Nuova</button>}</div>
                <SearchSelect options={biciOpts} value={f.bici_id} onChange={(v) => set("bici_id", v)} placeholder={f.cliente_id ? "Seleziona bici..." : "Prima scegli il cliente"} testId="scheda-bici-select" emptyText="Nessuna bici: aggiungine una" />
              </div>
            </div>
            )}
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <div><L>Data ingresso</L><Input type="date" value={f.data_ingresso} onChange={(e) => set("data_ingresso", e.target.value)} data-testid="scheda-data-ingresso-input" /></div>
              <div><L>Consegna prevista</L><Input type="date" value={f.data_consegna_prevista} onChange={(e) => set("data_consegna_prevista", e.target.value)} data-testid="scheda-data-consegna-input" /></div>
            </div>
          </Section>
          <Section title="Problema e lavori" testId="scheda-section-lavori">
            <div className="space-y-4">
              <div><L>Problema riscontrato / richiesta cliente</L><Textarea rows={3} value={f.problema} onChange={(e) => set("problema", e.target.value)} data-testid="scheda-problema-input" /></div>
              <div><L>Lavori eseguiti</L><Textarea rows={3} value={f.lavori_eseguiti} onChange={(e) => set("lavori_eseguiti", e.target.value)} data-testid="scheda-lavori-input" /></div>
            </div>
          </Section>
          <Section title="Ricambi utilizzati" testId="scheda-section-ricambi"><RicambiRows rows={f.ricambi} setRows={(v) => set("ricambi", v)} magazzino={magazzino} /></Section>
          <Section title="Manodopera" testId="scheda-section-manodopera"><ManodoperaRows rows={f.manodopera} setRows={(v) => set("manodopera", v)} tariffa={tariffa} /></Section>
          <Section title="Note interne"><Textarea rows={2} value={f.note} onChange={(e) => set("note", e.target.value)} data-testid="scheda-note-input" /></Section>
        </div>

        <aside className="space-y-4 xl:sticky xl:top-24 xl:self-start">
          <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
            <L>Stato riparazione</L>
            <Select value={f.stato} onValueChange={(v) => set("stato", v)}>
              <SelectTrigger data-testid="scheda-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>{STATI.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="rounded-lg bg-slate-900 p-5 text-white shadow-sm">
            <div className="flex justify-between py-1.5 text-sm text-slate-300"><span>Ricambi</span><span className="font-mono" data-testid="totale-ricambi-display">{euro(totR)}</span></div>
            <div className="flex justify-between border-b border-slate-700 py-1.5 text-sm text-slate-300"><span>Manodopera</span><span className="font-mono" data-testid="totale-manodopera-display">{euro(totM)}</span></div>
            <div className="flex items-end justify-between pt-4"><span className="text-xs font-bold uppercase tracking-wider text-orange-400">Totale scheda</span><span className="font-mono text-3xl font-bold" data-testid="totale-scheda-display">{euro(totR + totM)}</span></div>
            <p className="mt-2 text-[11px] text-slate-400">Prezzi IVA inclusa. I ricambi vengono scalati dal magazzino al salvataggio.</p>
          </div>
        </aside>
      </div>

      <EntityFormDialog open={dlg === "cliente"} onOpenChange={(o) => !o && setDlg(null)} endpoint="clienti" fields={CLIENTE_FIELDS} title="Nuovo cliente" prefix="scheda-cliente-form"
        onSaved={(c) => { loadRefs(); setF((s) => ({ ...s, cliente_id: c.id, bici_id: "" })); }} />
      <EntityFormDialog open={dlg === "bici"} onOpenChange={(o) => !o && setDlg(null)} endpoint="bici" fields={biciFields(clienti.filter((c) => c.id === f.cliente_id))} item={{ cliente_id: f.cliente_id }} title="Nuova bici" prefix="scheda-bici-form"
        onSaved={(b) => { loadRefs(); set("bici_id", b.id); }} />
    </div>
  );
}
